/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import React, { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import ReactTooltip from "react-tooltip";
import { useRecoilState } from "recoil";
import { updateUser } from "../../api/User";
import { SettingsHelpVideosAtom } from "../../atoms/SettingsHelpVideos";
import { UserAtom } from "../../atoms/UserAtom";
import { IStartPageOptions } from "../../generated/Api";
import { customTooltip } from "../../styles/general";
import { IOption } from "../../types/CommonTypes";
import { HelpVideoNameMap, IHelpContexts } from "../../types/HelpVideosTypes";
import getImageUrl from "../../utils/GetImageURL";
import readFile from "../../utils/ReadFile";
import { removeDiacritics } from "../../utils/RemoveDiacritics";
import AvatarIcon from "../Avatar";
import UpdateAppName from "../Modal/container/UpdateAppName";
import UpdateCanSpam from "../Modal/container/UpdateCanSpam";
import UpdateName from "../Modal/container/UpdateName";
import UpdateProfilePhoto from "../Modal/container/UpdateProfilePhoto";
import Warning from "../Modal/container/Warning";
import { useModal } from "../Modal/Modal";
import { TooltipText } from "../SideBar";
import StyledAutocompleteSelect from "../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../StyledButton";
import StyledInput from "../StyledInput";
import Switch from "../Switch";
import ArchiveConfig from "./ArchiveConfig";
import SendConfirmationConfig from "./SendConfirmationConfig";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  .divider {
    width: 100%;
    height: 1px;

    background: #eaeaea;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  display: flex;
`;

const LabelText = styled.div`
  color: #222222;
  font-weight: 400;
  font-size: 16px;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
  }
`;

const FormContent = styled.div`
  width: 100%;
  height: fit-content;

  padding: 0 14px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  grid-gap: 24px;

  .divider {
    width: 100%;
    height: 1px;
    background-color: #eaeaeabf;

    margin: 24px 0;
  }
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1140px) {
    grid-gap: 32px;
  }

  .styled-auto-complete-wrapper {
    width: fit-content;
  }

  .grid-wrapper {
    display: grid;
    grid-template-columns: 280px 1fr;
    grid-gap: 32px;

    &.center {
      align-items: center;
    }

    @media screen and (max-width: 1140px) {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      grid-gap: 16px;
    }
  }

  .flex-column-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    grid-gap: 8px;
  }
`;

const InfoTooltipIcon = styled.div`
  cursor: pointer;
  margin: 3px 0px 0px 3px;
  :hover {
    color: #fb972e;
  }
`;

const InfoTooltipContent = styled.div`
  position: absolute;
  height: fit-content;
  background: white;
  z-index: 20;
  padding: 18px 14px 23px 22px;
  box-shadow: 0px 3px 20px #00000026;
  border: 1px solid #ededed;
  border-radius: 4px;
  top: 0;
  left: 25px;
  width: 200px;
`;

const InfoTitle = styled.div`
  font: normal normal normal 13px Source Sans Pro;
  color: #707070;
  margin-bottom: 8px;
`;

const InfoContent = styled.div``;

export function formatArchiveName(text: string) {
  text = text.toLowerCase().trim();
  text = text
    .split("")
    .map((c) => {
      if (c === "-") return c;
      if (c.localeCompare("a") !== -1 && c.localeCompare("z") !== 1) return c;
      if (c.localeCompare("0") !== -1 && c.localeCompare("9") !== 1) return c;
    })
    .filter((c) => c)
    .join("");

  return removeDiacritics(text);
}

export enum IStastEmailFrequency {
  NONE = "NONE",
  DAILY = "DAILY",
  WEEKLY = "WEEKLY",
}

export const startPageOptions = [
  {
    name: "Send Broadcast",
    value: IStartPageOptions.SEND_BROADCAST,
  },
  {
    name: "All Emails",
    value: IStartPageOptions.ALL_EMAILS,
  },
  {
    name: "Dashboard",
    value: IStartPageOptions.DASHBOARD,
  },
];

const GeneralWrapperContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
`;

const GeneralWrapperWrapper = styled.div`
  width: 100%;

  padding: 40px 50px 48px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
`;

interface GeneralWrapperProps {
  children: ReactNode;
  title: string;
  description: string;
}

function GeneralWrapper({ children, title, description }: GeneralWrapperProps) {
  return (
    <GeneralWrapperContainer>
      <GeneralWrapperWrapper>
        <TextWrapper>
          <MainTitle>{title}</MainTitle>
          <MainText>{description}</MainText>
        </TextWrapper>

        <FormContent>{children}</FormContent>
      </GeneralWrapperWrapper>
    </GeneralWrapperContainer>
  );
}

export default function General(): JSX.Element | null {
  const router = useRouter();
  const modal = useModal();
  const imageRef = useRef<HTMLInputElement>(null);
  const [image, setImage] = useState<Blob | null>(null);
  const [user, setUser] = useRecoilState(UserAtom);

  let ifHasQueryParams = Object.keys(router.query).length !== 0;

  function handleSmoothScroll() {
    const destination = document?.querySelector("#automatic-stats-email");
    if (destination) {
      destination.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  useEffect(() => {
    if (ifHasQueryParams) {
      handleSmoothScroll();
    }
  }, []);

  const [days, setDays] = useState<number>();
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [, setHelpVideo] = useRecoilState(SettingsHelpVideosAtom);
  const [startPageSelected, setStartPageOptions] = useState<IOption>({
    value: "",
    name: "",
  });

  useEffect(() => {
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.SettingsGeneral],
      helpContext: IHelpContexts.SettingsGeneral,
    });
  }, []);

  useEffect(() => {
    if (!user) return;

    const option = startPageOptions.find(
      (e) => e.value === user.options.startPage
    );

    setStartPageOptions(option);
    setDays(user.options.autoListOptions?.frequencyDays);
  }, [user?.userUid]);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const imageDataUrl: string = await readFile(file);

      modal.pushModal(
        <UpdateProfilePhoto
          imageSrc={imageDataUrl}
          closeModal={() => modal.popModal()}
          saveImage={async (croppedImage: Blob) => {
            setImage(croppedImage);
          }}
        />,
        "Update Your Profile Photo"
      );
    }
  };

  if (!user) return null;

  return (
    <Container>
      <GeneralWrapper
        title="Basics"
        description="Please provide the following personal information in the area below.
      These details will be used for account login and authentication."
      >
        <Fragment>
          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>Profile Photo</SecondaryTitle>

              <AvatarIcon
                absoluteURL={
                  image ? URL.createObjectURL(image) : getImageUrl(user.avatar)
                }
                size={32}
                userName={user.firstName}
              />
            </div>

            <StyledButton
              inverted
              css={css`
                width: 100px;
              `}
              onClick={() => {
                imageRef.current?.click();
              }}
            >
              <input
                ref={imageRef}
                type="file"
                accept="image/*"
                onChange={onFileChange}
                style={{ display: "none" }}
              />
              Edit
            </StyledButton>
          </SpaceBetween>

          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>Full Name</SecondaryTitle>

              <LabelText>
                {user.firstName} {user.lastName}
              </LabelText>
            </div>

            <StyledButton
              inverted
              css={css`
                width: 100px;
              `}
              onClick={() => {
                modal.pushModal(<UpdateName />, "Update Your Name");
              }}
            >
              Edit
            </StyledButton>
          </SpaceBetween>

          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>Unique App Name</SecondaryTitle>

              <LabelText>{user.userAppName}</LabelText>
            </div>

            <StyledButton
              inverted
              css={css`
                width: 100px;
              `}
              onClick={() =>
                modal.pushModal(
                  <div
                    css={css`
                      .css-l8r6cn > button:last-child {
                        background-color: #c41700;
                        border-color: #c41700;
                      }
                    `}
                  >
                    <Warning
                      title="Warning"
                      icon={"./unique-app-warning.svg"}
                      description={
                        <div
                          css={css`
                            padding: 0px 40px;
                          `}
                        >
                          <span>
                            Changing your unique app name will also change the
                            links for your email archive page and form URLS.
                          </span>
                          <span>&nbsp;Do you still wish to proceed?</span>
                        </div>
                      }
                      buttonsName={["Cancel", "Proceed"]}
                      onCancelClicked={() => modal.popModal()}
                      onOkClicked={() => {
                        modal.popModal();
                        modal.pushModal(<UpdateAppName />, "Update App Name");
                      }}
                    />
                  </div>
                )
              }
            >
              Edit
            </StyledButton>
          </SpaceBetween>

          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>
                CAN-SPAM
                <div style={{ position: "relative", marginLeft: "6px" }}>
                  <InfoTooltipIcon
                    onMouseEnter={() => setShowInfoTooltip(true)}
                    onMouseLeave={() => setShowInfoTooltip(false)}
                  >
                    <AiOutlineInfoCircle color="#BBBBBB" />
                  </InfoTooltipIcon>
                  {showInfoTooltip && (
                    <InfoTooltipContent>
                      <InfoTitle>CAN-SPAM</InfoTitle>
                      <InfoContent>
                        The CAN-SPAM goes into your email footer.
                      </InfoContent>
                    </InfoTooltipContent>
                  )}
                </div>
                {/* <SubTitle style={{ opacity: 0.6, paddingRight: 20, marginTop: 5 }}>
              The CAN-SPAM goes into your email footer
            </SubTitle> */}
              </SecondaryTitle>

              <LabelText>
                {user.company}
                <br />
                {user.address}
                {user.companyPhone && (
                  <>
                    <br />
                    <span>{user.companyPhone}</span>
                  </>
                )}
              </LabelText>
            </div>

            <StyledButton
              inverted
              css={css`
                width: 100px;
              `}
              onClick={() => {
                modal.pushModal(<UpdateCanSpam />, "Update Your CAN-SPAM");
              }}
            >
              Edit
            </StyledButton>
          </SpaceBetween>

          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>Email Address</SecondaryTitle>

              <LabelText>{user.email}</LabelText>
            </div>

            <div
              style={{ cursor: "pointer" }}
              data-tip
              data-for="editEmailAddress"
            >
              <ReactTooltip
                id="editEmailAddress"
                place="top"
                effect="solid"
                globalEventOff="click"
                backgroundColor="#333333"
                css={css`
                  ${customTooltip}
                `}
              >
                <TooltipText>Contact us via Helpdesk</TooltipText>
              </ReactTooltip>

              <StyledButton
                inverted
                css={css`
                  width: 100px;
                `}
                onClick={() => {
                  // modal.pushModal(<UpdateEmail />, "Update Your Address");
                }}
                disable
              >
                Edit
              </StyledButton>
            </div>
          </SpaceBetween>

          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>Phone Number</SecondaryTitle>

              <LabelText>{user.phone}</LabelText>
            </div>

            <div style={{ cursor: "pointer" }} data-tip data-for="phoneEdit">
              <ReactTooltip
                id="phoneEdit"
                place="top"
                effect="solid"
                globalEventOff="click"
                backgroundColor="#333333"
                css={css`
                  ${customTooltip}
                `}
              >
                <TooltipText>Contact us via Helpdesk</TooltipText>
              </ReactTooltip>

              <StyledButton
                inverted
                css={css`
                  width: 100px;
                `}
                disable
                // onClick={() => {
                //   modal.pushModal(<UpdatePhone />, "Update Your Phone Number");
                // }}
              >
                Edit
              </StyledButton>
            </div>
          </SpaceBetween>

          <SendConfirmationConfig />
        </Fragment>
      </GeneralWrapper>

      <div className="divider" />

      <GeneralWrapper
        title="Customization"
        description="Customize the platform for better usability."
      >
        <Fragment>
          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Start Page</SecondaryTitle>
              <MainText>
                Select the page where you want to start at when you login.
              </MainText>
            </div>

            <div className="styled-auto-complete-wrapper">
              <StyledAutocompleteSelect
                placeholder={"Select an option"}
                options={startPageOptions}
                selectedOption={startPageSelected}
                onOptionSelect={async (option) => {
                  if (option.value === "") return;

                  setStartPageOptions(option);

                  const userObject = {
                    ...user,
                    options: {
                      ...user.options,
                      startPage: option.value as IStartPageOptions,
                    },
                  };

                  delete (userObject as any)._id;

                  const success = await updateUser(userObject);
                  if (success)
                    setUser(
                      (u) =>
                        ({
                          ...u,
                          options: {
                            ...u?.options,
                            startPage: option.value as IStartPageOptions,
                          },
                        } as any)
                    );
                }}
              />
            </div>
          </SpaceBetween>
        </Fragment>
      </GeneralWrapper>

      <div className="divider" />

      <GeneralWrapper
        title="Automatic List Backup"
        description="Automatically receive an email with a download link to keep a backup
        of your contact list."
      >
        <Fragment>
          <SpaceBetween>
            <div className="grid-wrapper center">
              <SecondaryTitle>Last Backup</SecondaryTitle>

              <LabelText> {dayjs(new Date()).format("MMM D, YYYY")}</LabelText>
            </div>
          </SpaceBetween>

          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Period</SecondaryTitle>
              <MainText>
                Select the number of days between each backup.
              </MainText>
            </div>

            <StyledInput
              css={css`
                width: 100px;
              `}
              value={days}
              min="0"
              step="1"
              placeholder="Days"
              type="number"
              onChange={async (event) => {
                const newDays = parseInt(event.target.value, 10);

                if (newDays <= 0) return;
                setDays(newDays);

                const userObject = {
                  ...user,
                  options: {
                    ...user.options,
                    autoListOptions: {
                      ...user.options.autoListOptions,
                      frequencyDays: newDays,
                    },
                  },
                };

                delete (userObject as any)._id;

                const success = await updateUser(userObject);
                if (success) {
                  setUser(
                    (u) =>
                      ({
                        ...u,
                        options: {
                          ...u.options,
                          autoListOptions: {
                            ...u.options.autoListOptions,
                            frequencyDays: parseInt(event.target.value, 10),
                          },
                        },
                      } as any)
                  );
                }
              }}
            />
          </SpaceBetween>
        </Fragment>
      </GeneralWrapper>

      <div className="divider" />

      <GeneralWrapper
        title="Automatic Stats Email"
        description="Configuration of your stats automated email delivery."
      >
        <Fragment>
          <SpaceBetween id="automatic-stats-email">
            <div className="grid-wrapper">
              <SecondaryTitle>Daily</SecondaryTitle>
            </div>

            <Switch
              toggled={!!user.options?.statsEmail?.receiveDailyEmail}
              whenToggled={async (vle) => {
                const userObject = {
                  ...user,
                  options: {
                    ...user.options,
                    statsEmail: {
                      ...user.options.statsEmail,
                      receiveDailyEmail: vle,
                    },
                  },
                };

                delete (userObject as any)._id;

                const success = await updateUser(userObject);
                if (success)
                  setUser(
                    (u) =>
                      ({
                        ...u,
                        options: {
                          ...u.options,
                          statsEmail: {
                            ...u.options.statsEmail,
                            receiveDailyEmail: vle,
                          },
                        },
                      } as any)
                  );
              }}
            />
          </SpaceBetween>

          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>Weekly</SecondaryTitle>
            </div>

            <Switch
              toggled={!!user.options?.statsEmail?.receiveWeekEmail}
              whenToggled={async (vle) => {
                const userObject = {
                  ...user,
                  options: {
                    ...user.options,
                    statsEmail: {
                      ...user.options.statsEmail,
                      receiveWeekEmail: vle,
                    },
                  },
                };

                delete (userObject as any)._id;

                const success = await updateUser(userObject);
                if (success)
                  setUser(
                    (u) =>
                      ({
                        ...u,
                        options: {
                          ...u.options,
                          statsEmail: {
                            ...u.options.statsEmail,
                            receiveWeekEmail: vle,
                          },
                        },
                      } as any)
                  );
              }}
            />
          </SpaceBetween>
        </Fragment>
      </GeneralWrapper>

      <div className="divider" />

      <GeneralWrapper
        title="Public Archive"
        description="Show your email history in a public archive."
      >
        <Fragment>
          <ArchiveConfig />
        </Fragment>
      </GeneralWrapper>
    </Container>
  );
}
